import React, { useState, useCallback, useEffect } from "react";
import {
  FaSearch,
  FaSpinner,
  FaExternalLinkAlt,
  FaFilter,
  FaSave,
  FaBookmark,
  FaTrash,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";

const LANGFUSE_PUBLIC_KEY = "pk-lf-87c2a037-c108-4842-be02-7cf71bb09a81";
const LANGFUSE_SECRET_KEY = "sk-lf-4dce5fc7-ee17-4e6c-b09b-302b171a45d3";
const LANGFUSE_BASE_URL = "https://us.cloud.langfuse.com";
const LANGFUSE_PROJECT_ID = "cmjr4rgsz089jad08br7h26ed";

const SAVED_LOGS_COLLECTION = "adminSavedTraces";

const TASK_TYPES = [
  "Income/Employment",
  "Asset",
  "Credit",
  "Property",
  "Borrower Info",
  "Disclosures",
  "Initial Disclosures",
];

const STEP_LABELS = {
  selectRelevantDocumentsForTask: "Select Relevant Docs",
  step1PerformLoanTask: "step1 - Perform Task",
  "step3GetTheUIRepresentation-templateIntegration": "step3 - UI Representation",
  "step3GetTheUIRepresentation-freshGeneration": "step3 - UI (Fresh)",
  consolidateClassifiedChunks: "Document Classification",
  "consolidateClassifiedChunks-gemini-fallback":
    "Document Classification (Gemini Fallback)",
  summarizeLoanProcessingChecklist: "Checklist Summary",
};

const getStepLabel = (trace) => {
  const endpoint = trace.metadata?.endpoint || "";
  return STEP_LABELS[endpoint] || trace.name || "-";
};

const getModel = (tags) => {
  if (!Array.isArray(tags)) return "-";
  const model = tags.find(
    (t) => t.includes("gemini") || t.includes("gpt") || t.includes("claude")
  );
  return model || "-";
};

const getDefaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const getDefaultTo = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const traceUrl = (traceId) =>
  `${LANGFUSE_BASE_URL}/project/${LANGFUSE_PROJECT_ID}/traces/${traceId}`;

// ─── Traces Tab ─────────────────────────────────────────────────────────────

const TracesTab = ({ onSave }) => {
  const [orgId, setOrgId] = useState("");
  const [loanId, setLoanId] = useState("");
  const [taskFilter, setTaskFilter] = useState("");
  const [fromDate, setFromDate] = useState(getDefaultFrom);
  const [toDate, setToDate] = useState(getDefaultTo);
  const [traces, setTraces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [saveNotes, setSaveNotes] = useState("");

  const fetchTraces = useCallback(async () => {
    if (!orgId.trim() || !loanId.trim()) {
      toast.warning("Both Org ID and Loan ID are required");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      const params = new URLSearchParams({ limit: "100" });
      params.append("tags", `loan:${loanId.trim()}`);
      params.append("tags", `org:${orgId.trim()}`);
      params.append("fromTimestamp", new Date(fromDate).toISOString());
      params.append("toTimestamp", new Date(toDate).toISOString());

      const res = await fetch(
        `${LANGFUSE_BASE_URL}/api/public/traces?${params}`,
        {
          headers: {
            Authorization:
              "Basic " + btoa(`${LANGFUSE_PUBLIC_KEY}:${LANGFUSE_SECRET_KEY}`),
          },
        }
      );

      const data = await res.json();

      if (data.error) {
        toast.error(data.message || "Langfuse query failed");
        setTraces([]);
        return;
      }

      setTraces(data.data || []);

      if ((data.data || []).length === 0) {
        toast.info("No traces found for the given filters");
      }
    } catch (err) {
      toast.error("Failed to fetch traces: " + err.message);
      setTraces([]);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, loanId, fromDate, toDate]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") fetchTraces();
  };

  const openSaveModal = () => {
    if (filteredTraces.length === 0) return;
    setSaveTitle(`${orgId} / ${loanId}${taskFilter ? ` / ${taskFilter}` : ""}`);
    setSaveNotes("");
    setShowSaveModal(true);
  };

  const handleSave = async () => {
    if (!saveTitle.trim()) {
      toast.warning("Please enter a title");
      return;
    }
    setShowSaveModal(false);
    await onSave({
      label: saveTitle.trim(),
      notes: saveNotes.trim() || null,
      orgId,
      loanId,
      taskFilter,
      fromDate,
      toDate,
      traceCount: filteredTraces.length,
      traces: filteredTraces.map((t) => ({
        id: t.id,
        name: t.name,
        timestamp: t.timestamp,
        tags: t.tags,
        taskTitle: t.metadata?.taskTitle || null,
        endpoint: t.metadata?.endpoint || null,
        fallbackFrom: t.metadata?.fallbackFrom || null,
      })),
    });
  };

  // Client-side filter by checklist type
  const filteredTraces = taskFilter
    ? traces.filter((t) => {
        const title = (t.metadata?.taskTitle || "").toLowerCase();
        return title.includes(taskFilter.toLowerCase());
      })
    : traces;

  // Build summary from filtered traces
  const summary = (() => {
    if (filteredTraces.length === 0) return null;
    const taskCounts = {};
    const models = new Set();
    let fallbackCount = 0;

    filteredTraces.forEach((t) => {
      const task = t.metadata?.taskTitle || t.name || "Unknown";
      taskCounts[task] = (taskCounts[task] || 0) + 1;
      const model = getModel(t.tags);
      if (model !== "-") models.add(model);
      if (t.metadata?.fallbackFrom) fallbackCount++;
    });

    const timestamps = filteredTraces.map((t) =>
      new Date(t.timestamp).getTime()
    );
    const earliest = new Date(Math.min(...timestamps));
    const latest = new Date(Math.max(...timestamps));

    return { taskCounts, models: [...models], fallbackCount, earliest, latest };
  })();

  return (
    <>
      {/* Filters Card */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">
            <FaFilter className="me-2" />
            Filters
          </h5>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label htmlFor="orgIdInput" className="form-label">
                Org ID <span className="text-danger">*</span>
              </label>
              <input
                id="orgIdInput"
                type="text"
                className="form-control"
                placeholder="e.g. FhxSPuNQckog..."
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                onKeyDown={handleKeyDown}
                required
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="loanIdInput" className="form-label">
                Loan ID <span className="text-danger">*</span>
              </label>
              <input
                id="loanIdInput"
                type="text"
                className="form-control"
                placeholder="e.g. CHCb82vhMgQc..."
                value={loanId}
                onChange={(e) => setLoanId(e.target.value)}
                onKeyDown={handleKeyDown}
                required
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="taskFilter" className="form-label">
                Checklist Type
              </label>
              <select
                id="taskFilter"
                className="form-select"
                value={taskFilter}
                onChange={(e) => setTaskFilter(e.target.value)}
              >
                <option value="">All</option>
                {TASK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label htmlFor="fromDate" className="form-label">
                From
              </label>
              <input
                id="fromDate"
                type="datetime-local"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="toDate" className="form-label">
                To
              </label>
              <input
                id="toDate"
                type="datetime-local"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={fetchTraces}
                disabled={isLoading}
                style={{ background: "#6366f1", borderColor: "#6366f1" }}
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="me-2 fa-spin" /> Searching...
                  </>
                ) : (
                  <>
                    <FaSearch className="me-2" /> Search
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {hasSearched && !isLoading && filteredTraces.length === 0 && (
        <div className="card">
          <div className="card-body text-center py-5">
            <p className="mb-1" style={{ fontSize: 16 }}>
              No traces found
            </p>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              Check that the Org ID / Loan ID are correct and the date range
              covers the expected time.
            </p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="card">
          <div className="card-body text-center py-5">
            <FaSpinner
              className="fa-spin mb-3"
              style={{ fontSize: 24, color: "#6366f1" }}
            />
            <p className="text-muted mb-0">Fetching traces from Langfuse...</p>
          </div>
        </div>
      )}

      {/* Results */}
      {filteredTraces.length > 0 && (
        <>
          {/* Summary cards */}
          {summary && (
            <div className="row mb-3">
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body py-2">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Total Traces
                    </div>
                    <h4 className="mb-0">{filteredTraces.length}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body py-2">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Models
                    </div>
                    <h6 className="mb-0">
                      {summary.models.join(", ") || "-"}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div
                  className={`card text-center ${summary.fallbackCount > 0 ? "border-warning" : ""}`}
                >
                  <div className="card-body py-2">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Fallbacks
                    </div>
                    <h4
                      className={`mb-0 ${summary.fallbackCount > 0 ? "text-warning" : ""}`}
                    >
                      {summary.fallbackCount}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body py-2">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Time Range
                    </div>
                    <div style={{ fontSize: 13 }}>
                      {summary.earliest.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      —{" "}
                      {summary.latest.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Traces table */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Traces</h5>
              <div className="d-flex gap-2 align-items-center">
                <span className="badge bg-secondary">
                  {filteredTraces.length} results
                </span>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={openSaveModal}
                  style={{ borderColor: "#6366f1", color: "#6366f1" }}
                >
                  <FaSave className="me-1" /> Save Log
                </button>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Time (UTC)</th>
                    <th>Task</th>
                    <th>Step</th>
                    <th>Fallback</th>
                    <th>Trace</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTraces.map((t) => {
                    const ts = new Date(t.timestamp);
                    const timeStr = ts.toISOString().slice(11, 16);
                    const dateStr = ts.toISOString().slice(0, 10);
                    const task = t.metadata?.taskTitle || "-";
                    const step = getStepLabel(t);
                    const fallback = t.metadata?.fallbackFrom || "-";

                    return (
                      <tr key={t.id}>
                        <td>
                          <span
                            className="text-muted me-1"
                            style={{ fontSize: 11 }}
                          >
                            {dateStr}
                          </span>
                          {timeStr}
                        </td>
                        <td>
                          {task !== "-" ? (
                            <span className="badge bg-info text-dark">
                              {task}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{step}</td>
                        <td>
                          {fallback !== "-" ? (
                            <span className="badge bg-warning text-dark">
                              {fallback}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <a
                            href={traceUrl(t.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#6366f1", textDecoration: "none" }}
                          >
                            view{" "}
                            <FaExternalLinkAlt style={{ fontSize: 10 }} />
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Task breakdown */}
          {summary && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Task Breakdown</h5>
              </div>
              <div className="card-body">
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(summary.taskCounts)
                    .sort(([, a], [, b]) => b - a)
                    .map(([task, count]) => (
                      <span
                        key={task}
                        className="badge bg-light text-dark border"
                        style={{ fontSize: 13, padding: "6px 12px" }}
                      >
                        {task}: <strong>{count}</strong>
                      </span>
                    ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowSaveModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Save Log</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSaveModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="saveTitle" className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    id="saveTitle"
                    type="text"
                    className="form-control"
                    placeholder="e.g. Income issue for loan XYZ"
                    value={saveTitle}
                    onChange={(e) => setSaveTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSave();
                    }}
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="saveNotes" className="form-label">
                    Notes <span className="text-muted">(optional)</span>
                  </label>
                  <textarea
                    id="saveNotes"
                    className="form-control"
                    rows={3}
                    placeholder="Add context, observations, or next steps..."
                    value={saveNotes}
                    onChange={(e) => setSaveNotes(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSaveModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  style={{ background: "#6366f1", borderColor: "#6366f1" }}
                >
                  <FaSave className="me-1" /> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Saved Logs Tab ─────────────────────────────────────────────────────────

const SavedLogsTab = ({ savedLogs, isLoadingSaved, onDelete }) => {
  const [expandedId, setExpandedId] = useState(null);

  if (isLoadingSaved) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <FaSpinner
            className="fa-spin mb-3"
            style={{ fontSize: 24, color: "#6366f1" }}
          />
          <p className="text-muted mb-0">Loading saved logs...</p>
        </div>
      </div>
    );
  }

  if (savedLogs.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <FaBookmark className="mb-3" style={{ fontSize: 24, color: "#94a3b8" }} />
          <p className="mb-1" style={{ fontSize: 16 }}>
            No saved logs yet
          </p>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            Search for traces in the Traces tab, then click "Save Log" to save them here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {savedLogs.map((log) => {
        const isExpanded = expandedId === log.id;
        const savedAt = log.savedAt?.toDate
          ? log.savedAt.toDate().toLocaleString()
          : "Unknown";

        return (
          <div className="card" key={log.id}>
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => setExpandedId(isExpanded ? null : log.id)}
            >
              <div>
                <h6 className="mb-0">{log.label}</h6>
                <small className="text-muted">
                  {log.traceCount} traces — saved {savedAt}
                </small>
                {log.notes && (
                  <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                    {log.notes}
                  </div>
                )}
              </div>
              <div className="d-flex gap-2 align-items-center">
                <span className="badge bg-secondary">
                  {log.traceCount} traces
                </span>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(log.id);
                  }}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
            {isExpanded && (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Time (UTC)</th>
                      <th>Task</th>
                      <th>Step</th>
                        <th>Fallback</th>
                      <th>Trace</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(log.traces || []).map((t) => {
                      const ts = new Date(t.timestamp);
                      const timeStr = ts.toISOString().slice(11, 16);
                      const dateStr = ts.toISOString().slice(0, 10);
                      const task = t.taskTitle || "-";
                      const endpoint = t.endpoint || "";
                      const step =
                        STEP_LABELS[endpoint] || t.name || "-";
                      const fallback = t.fallbackFrom || "-";

                      return (
                        <tr key={t.id}>
                          <td>
                            <span
                              className="text-muted me-1"
                              style={{ fontSize: 11 }}
                            >
                              {dateStr}
                            </span>
                            {timeStr}
                          </td>
                          <td>
                            {task !== "-" ? (
                              <span className="badge bg-info text-dark">
                                {task}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{step}</td>
                          <td>
                            {fallback !== "-" ? (
                              <span className="badge bg-warning text-dark">
                                {fallback}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <a
                              href={traceUrl(t.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#6366f1",
                                textDecoration: "none",
                              }}
                            >
                              view{" "}
                              <FaExternalLinkAlt style={{ fontSize: 10 }} />
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────

const EvalsPage = () => {
  const [activeTab, setActiveTab] = useState("traces");
  const [savedLogs, setSavedLogs] = useState([]);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);

  const fetchSavedLogs = useCallback(async () => {
    setIsLoadingSaved(true);
    try {
      const q = query(
        collection(firestoreProd, SAVED_LOGS_COLLECTION),
        orderBy("savedAt", "desc")
      );
      const snapshot = await getDocs(q);
      const logs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSavedLogs(logs);
    } catch (err) {
      console.error("Failed to load saved logs:", err);
      toast.error("Failed to load saved logs");
    } finally {
      setIsLoadingSaved(false);
    }
  }, []);

  useEffect(() => {
    fetchSavedLogs();
  }, [fetchSavedLogs]);

  const handleSave = async (logData) => {
    try {
      await addDoc(collection(firestoreProd, SAVED_LOGS_COLLECTION), {
        ...logData,
        savedAt: serverTimestamp(),
      });
      toast.success("Log saved successfully");
      fetchSavedLogs();
      setActiveTab("saved");
    } catch (err) {
      toast.error("Failed to save log: " + err.message);
    }
  };

  const handleDelete = async (logId) => {
    try {
      await deleteDoc(doc(firestoreProd, SAVED_LOGS_COLLECTION, logId));
      toast.success("Log deleted");
      setSavedLogs((prev) => prev.filter((l) => l.id !== logId));
    } catch (err) {
      toast.error("Failed to delete log: " + err.message);
    }
  };

  return (
    <div className="container-fluid p-4 EvalsPageContainer">
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        draggable
        pauseOnHover
        theme="dark"
      />

      <h1 className="mb-1">Evals — Langfuse Trace Lookup</h1>
      <p className="text-muted mb-4">
        Search LLM traces by organization, loan, and date range.
      </p>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "traces" ? "active" : ""}`}
            onClick={() => setActiveTab("traces")}
            style={
              activeTab === "traces"
                ? { color: "#6366f1", borderColor: "#6366f1 #6366f1 #fff" }
                : {}
            }
          >
            <FaSearch className="me-2" />
            Traces
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "saved" ? "active" : ""}`}
            onClick={() => setActiveTab("saved")}
            style={
              activeTab === "saved"
                ? { color: "#6366f1", borderColor: "#6366f1 #6366f1 #fff" }
                : {}
            }
          >
            <FaBookmark className="me-2" />
            Saved Logs
            {savedLogs.length > 0 && (
              <span className="badge bg-secondary ms-2">
                {savedLogs.length}
              </span>
            )}
          </button>
        </li>
      </ul>

      {/* Tab content */}
      {activeTab === "traces" && <TracesTab onSave={handleSave} />}
      {activeTab === "saved" && (
        <SavedLogsTab
          savedLogs={savedLogs}
          isLoadingSaved={isLoadingSaved}
          onDelete={handleDelete}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .fa-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default EvalsPage;
